import { atom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { SyncStorage } from 'jotai/vanilla/utils/atomWithStorage'

import type { WARNING_STATE } from '@/features/web/step_counter/types'

export const STORAGE_ACCESS_KEY = 'STORAGE-ACCESS-KEY'
export const STORAGE_MOCK_ENABLED = 'STORAGE_MOCK-ENABLED'
export const STORAGE_TOOLTIP_CLICKED = 'STORAGE-TOOLTIP-CLICKED'
export const STORAGE_DGTOURCARD_TOOLTIP_CLICKED = 'STORAGE-DGTOURCARD-TOOLTIP-CLICKED'
export const STORAGE_POINT_TOOLTIP_CLICKED = 'STORAGE-POINT-TOOLTIP-CLICKED'
export const STORAGE_SHORTFORM_MANAGE_TOOLTIP_CLICKED = 'STORAGE-SHORTFORM-MANAGE-TOOLTIP-CLICKED'
export const STORAGE_SCHEME_URL = 'STORAGE-SCHEME-URL'
export const STORAGE_DAILY_QUIZ_DATE = 'STORAGE-DAILY-QUIZ-DATE'
export const STORAGE_ATTENDED_DATE = 'STORAGE-ATTENDANCE-DATE'
export const STORAGE_CLICKED_FOLLOWING_CONTENT_IDS = 'STORAGE-CLICKED-FOLLOWING-CONTENT-IDS'

export type GlobalState = {
  accessKey: string
  carrierName: string
  deviceId: string
  euk: string
  sessionId: string
  accessKeyExpired: boolean
  isSkeleton: boolean
  isMockEnabled: boolean
  schemeURL: string
  isTooltipClicked: boolean
  mission: string
  allServicesPageError: boolean
  tooltipMessage: string
  tooltipCurrentOrder: number
  stepCounterBanner: string
  attendedDate: string
  stepCounterWarningState: WARNING_STATE | string
}

export const globalState = atom<GlobalState & Record<string, any>>({
  accessKey: '',
  carrierName: '',
  deviceId: '',
  euk: '',
  sessionId: '',
  accessKeyExpired: false,
  isSkeleton: false,
  isMockEnabled: false,
  schemeURL: '',
  isTooltipClicked: false,
  mission: '',
  allServicesPageError: false,
  tooltipMessage: '',
  tooltipCurrentOrder: 1,
  stepCounterBanner: '',
  attendedDate: '',
  stepCounterWarningState: '',
})

export const errorBoundaryActiveAtom = atom(false)

export const accessKeyAtom = atom((get) => get(globalState).accessKey)
export const deviceInfoAtom = atom((get) => ({
  deviceId: get(globalState).deviceId,
  carrierName: get(globalState).carrierName,
}))

export const pedometerArrowPlayedAtom = atom(false)

export const eukAtom = atom((get) => get(globalState).euk)
export const sessionIdAtom = atom((get) => get(globalState).sessionId)
export const accessKeyExpiredAtom = atom((get) => get(globalState).accessKeyExpired)
export const isSkeletonAtom = atom((get) => get(globalState).isSkeleton)
export const isMockEnabledAtom = atom((get) => get(globalState).isMockEnabled)
export const schemeURLAtom = atom((get) => get(globalState).schemeURL)
export const isTooltipClickedAtom = atom((get) => get(globalState).isTooltipClicked)
export const missionTitleAtom = atom((get) => get(globalState).mission)
export const allServicesPageErrorAtom = atom((get) => get(globalState).allServicesPageError)
export const tooltipMessageAtom = atom((get) => get(globalState).tooltipMessage)
export const tooltipCurrentOrderAtom = atom((get) => get(globalState).tooltipCurrentOrder)
export const stepCounterWarningStateAtom = atom((get) => get(globalState).stepCounterWarningState)

const customStorage: SyncStorage<string | object> = {
  getItem: (key: string) => {
    const value = localStorage.getItem(key)
    try {
      return value ? JSON.parse(value) : '' // `undefined` 대신 빈 문자열 반환
    } catch {
      return '' // JSON 파싱 실패 시 기본 반환 값
    }
  },
  setItem: (key: string, newValue: string | object) => {
    const value = typeof newValue === 'object' ? JSON.stringify(newValue) : newValue
    localStorage.setItem(key, value)
  },
  removeItem: (key: string) => {
    localStorage.removeItem(key)
  },
}

const getInitialValue = (key: string, defaultValue: string | object | boolean) => {
  if (typeof window === 'undefined') {
    return defaultValue
  }

  const result = localStorage.getItem(key)

  if (!result) {
    return defaultValue
  }

  return typeof defaultValue === 'string' ? result : JSON.parse(result)
}

export const storageAccessKeyAtom = atomWithStorage<string | object>(
  STORAGE_ACCESS_KEY,
  getInitialValue(STORAGE_ACCESS_KEY, ''),
  customStorage
)

export const storageTooltipClicked = atomWithStorage(
  STORAGE_TOOLTIP_CLICKED,
  getInitialValue(STORAGE_TOOLTIP_CLICKED, false)
)

export const storageMockEnabled = atomWithStorage(
  STORAGE_MOCK_ENABLED,
  getInitialValue(STORAGE_MOCK_ENABLED, false)
)
export const storageSchemeUrl = atomWithStorage(
  STORAGE_SCHEME_URL,
  getInitialValue(STORAGE_SCHEME_URL, false)
)

export const storageDgTourCardTooltipClicked = atomWithStorage(
  STORAGE_DGTOURCARD_TOOLTIP_CLICKED,
  getInitialValue(STORAGE_DGTOURCARD_TOOLTIP_CLICKED, false)
)

export const storagePointTooltipClicked = atomWithStorage(
  STORAGE_POINT_TOOLTIP_CLICKED,
  getInitialValue(STORAGE_POINT_TOOLTIP_CLICKED, false)
)

export const storageShortFormManageTooltipClicked = atomWithStorage(
  STORAGE_SHORTFORM_MANAGE_TOOLTIP_CLICKED,
  getInitialValue(STORAGE_SHORTFORM_MANAGE_TOOLTIP_CLICKED, false)
)

export const storageDailyQuizDate = atomWithStorage(
  STORAGE_DAILY_QUIZ_DATE,
  getInitialValue(STORAGE_DAILY_QUIZ_DATE, '')
)

export const storageAttendedDate = atomWithStorage(
  STORAGE_ATTENDED_DATE,
  getInitialValue(STORAGE_ATTENDED_DATE, '')
)

export const storageClickedFollowingContentIdsAtom = atomWithStorage<string[]>(
  STORAGE_CLICKED_FOLLOWING_CONTENT_IDS,
  getInitialValue(STORAGE_CLICKED_FOLLOWING_CONTENT_IDS, []) as string[]
)
